import { motion } from "framer-motion";

const ABOUT_IMAGE = "/denis-portrait.png";

export const About = () => {
  const bridges = [
    { left: "Product decisions", right: "Revenue outcomes" },
    { left: "AI capability", right: "P&L impact" },
    { left: "Strategy", right: "Execution at scale" },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#050505] py-20 sm:py-28 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 lg:gap-16">
          {/* Left meta + portrait */}
          <div className="col-span-12 lg:col-span-3">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A880]" />
              <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880]">
                About / 01
              </span>
            </div>

            <div className="lg:sticky lg:top-32 mb-10 lg:mb-0 max-w-sm lg:max-w-none">
              <div className="relative border border-[#1f1f1f] bg-[#0a0a0a] p-2 sm:p-3">
                <img
                  src={ABOUT_IMAGE}
                  alt="Denis Antony"
                  className="block w-full max-w-full aspect-[4/5] object-cover"
                  data-testid="about-image"
                />
                <div className="absolute inset-2 sm:inset-3 ring-1 ring-[#C5A880]/10 pointer-events-none" />
              </div>
              <div className="mt-4 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[#666]">
                <span>Denis Antony</span>
              </div>
            </div>
          </div>

          {/* Right content */}
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              data-testid="about-heading"
              className="font-serif-display text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05] lg:leading-[1.02] tracking-tight text-[#F5F5F0]"
            >
              Thirty years building products that compound — at the
              intersection of{" "}
              <em className="italic text-[#C5A880]">strategy, AI</em> and
              growth.
            </motion.h2>

            {/* Lead narrative */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              data-testid="about-lead"
              className="mt-8 sm:mt-12 max-w-2xl space-y-5 sm:space-y-6 text-[15px] sm:text-base md:text-lg text-[#A3A39A] leading-relaxed"
            >
              <p>
                For 30+ years I've helped global contractors, infrastructure
                leaders and SaaS companies build, scale and modernize complex
                product ecosystems — across the UK, Europe, the US, the
                Middle East and APAC.
              </p>
              <p>
                I work with CEOs, founders and boards in construction and
                enterprise environments to bring clarity to product strategy,
                align AI initiatives with real business value, and accelerate
                decision-making at critical inflection points.
              </p>
              <p>
                I've spent my career at the intersection of construction,
                product and digital transformation — and one pattern has
                remained constant:
              </p>
            </motion.div>

            {/* Pull quote — manifesto */}
            <motion.figure
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              data-testid="about-pullquote"
              className="my-10 sm:my-14 md:my-16 relative max-w-3xl"
            >
              <span
                aria-hidden="true"
                className="font-serif-display text-[#C5A880]/30 text-7xl sm:text-8xl md:text-9xl leading-none absolute -top-4 -left-2 sm:-top-6 sm:-left-4 select-none pointer-events-none"
              >
                “
              </span>
              <div className="relative pl-6 sm:pl-10 border-l-2 border-[#C5A880]">
                <blockquote className="font-serif-display italic text-2xl sm:text-3xl md:text-5xl font-light leading-[1.15] text-[#F5F5F0]">
                  Technology alone doesn't create impact.
                  <span className="block mt-1 text-[#C5A880] not-italic font-light">
                    Clarity does.
                  </span>
                </blockquote>
              </div>
            </motion.figure>

            {/* Why this work */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.2 }}
              data-testid="about-why"
              className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl"
            >
              <div>
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880] mb-4">
                  What drives me
                </p>
                <p className="text-[15px] sm:text-base md:text-lg text-[#D8D8D0] leading-relaxed">
                  Solving complex, high-stakes problems where products don't
                  just get built — they{" "}
                  <span className="text-[#F5F5F0]">
                    change how industries operate
                  </span>
                  .
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880] mb-4">
                  Where I focus
                </p>
                <p className="text-[15px] sm:text-base md:text-lg text-[#D8D8D0] leading-relaxed">
                  Too many organizations invest heavily in technology, only to
                  struggle with adoption, fragmented experiences or unclear
                  direction. That{" "}
                  <span className="text-[#F5F5F0]">
                    gap between capability and real-world impact
                  </span>{" "}
                  is where I work.
                </p>
              </div>
            </motion.div>

            {/* Bridges */}
            <div className="mt-14 sm:mt-20 md:mt-24 border-t border-[#1a1a1a]">
              <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880] mt-8 mb-6">
                The connections I make
              </p>
              {bridges.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="grid grid-cols-1 sm:grid-cols-12 sm:items-center py-5 sm:py-6 border-t border-[#1a1a1a] group hover:bg-[#0c0c0c] transition-colors gap-1 sm:gap-0"
                  data-testid={`about-bridge-${i}`}
                >
                  <div className="sm:col-span-5 md:col-span-4 font-serif-display text-xl sm:text-2xl md:text-3xl font-light text-[#F5F5F0]">
                    {b.left}
                  </div>
                  <div className="sm:col-span-2 md:col-span-1 sm:text-center text-[#C5A880] tracking-widest text-sm sm:text-base">
                    →
                  </div>
                  <div className="sm:col-span-5 md:col-span-7 font-serif-display italic text-xl sm:text-2xl md:text-3xl font-light text-[#A3A39A] group-hover:text-[#F5F5F0] transition-colors">
                    {b.right}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
